import { env } from '@/config/env';
import {
  ApiError,
  ClientErrorCode,
  isProblemDetails,
  RATE_LIMITED_MESSAGE,
} from './errors';
import { newRequestId, REQUEST_ID_HEADER } from './request-id';
import type { ApiEnvelope } from './types';

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  timeoutMs?: number;
  /** Skip the refresh-and-retry on 401 (used by the auth endpoints themselves). */
  skipAuthRetry?: boolean;
  /**
   * RL-1911 review: a public call that must not carry the staff session
   * (no token, no branch, no refresh), e.g. a patient's shared record.
   */
  anonymous?: boolean;
  /** Upload a file as the raw body (e.g. consultation attachments). */
  rawBody?: Blob;
  /** Read the response as a file instead of the JSON envelope. */
  responseType?: 'blob';
  /** Browser HTTP cache mode (e.g. 'no-store' for audited patient images). */
  cache?: RequestCache;
  /**
   * RL-1658 review: let the request outlive the page (a Cancel / Done still
   * waiting for its Undo window when the tab is closed).
   */
  keepalive?: boolean;
}

export const CLIENT_HEADER = 'X-Ommri-Client';
export const BRANCH_HEADER = 'X-Branch-Id';

/**
 * RL-1176: the API refuses an X-Branch-Id the active role does not hold
 * (403 BRANCH_NOT_PERMITTED) or one that is not a UUID (400 BRANCH_ID_INVALID)
 * on every staff route (RL-1172); /auth/* ignores the header.
 */
export function isBranchRejection(status: number, code: string): boolean {
  return (
    (status === 403 && code === 'BRANCH_NOT_PERMITTED') ||
    (status === 400 && code === 'BRANCH_ID_INVALID')
  );
}

export interface ApiClientConfig {
  baseUrl: string;
  timeoutMs: number;
  fetchImpl?: typeof fetch;
  /** Bearer token for the current session (kept in memory, never in storage). */
  getAccessToken?: () => string | null | undefined;
  /** Branch selected in the topbar; omitted = all branches. */
  getBranchId?: () => string | null | undefined;
  /** Called when the API says the session is idle-locked (423 SESSION_LOCKED). */
  onSessionLocked?: () => void;
  /**
   * Called when the API refuses the request's X-Branch-Id (RL-1176), with the
   * branch that was sent. The request still fails; the handler reloads `me`
   * and refetches.
   */
  onBranchRejected?: (branchId: string | null) => void;
  /**
   * Called once on a 401: try to restore the session (refresh cookie). Return
   * true to retry the original request once, false to give up.
   */
  refreshSession?: () => Promise<boolean>;
}

function buildUrl(baseUrl: string, path: string, query?: Query): string {
  const url = `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '')
      params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/**
 * Central HTTP client for ommri-care-api. Adds X-Request-Id, the client
 * header, bearer token and branch header; handles timeouts; turns every
 * failure into ApiError; refreshes the session once on 401 and retries.
 */
export function createApiClient(config: ApiClientConfig) {
  const fetchImpl =
    config.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  async function send<T, M>(
    path: string,
    options: RequestOptions,
  ): Promise<ApiEnvelope<T, M>> {
    const requestId = newRequestId();
    const headers: Record<string, string> = {
      Accept: 'application/json, application/problem+json',
      [REQUEST_ID_HEADER]: requestId,
      [CLIENT_HEADER]: 'admin',
      ...options.headers,
    };
    if (options.rawBody)
      headers['Content-Type'] =
        options.rawBody.type || 'application/octet-stream';
    else if (options.body !== undefined)
      headers['Content-Type'] = 'application/json';
    const token = options.anonymous ? null : config.getAccessToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
    // A request may target another branch explicitly (e.g. booking at the
    // other branch from the New booking chips).
    const branchId = options.anonymous ? null : config.getBranchId?.();
    if (branchId && !options.headers?.[BRANCH_HEADER])
      headers[BRANCH_HEADER] = branchId;

    const timeout = new AbortController();
    const timer = setTimeout(
      () => timeout.abort(),
      options.timeoutMs ?? config.timeoutMs,
    );
    const onCallerAbort = () => timeout.abort();
    options.signal?.addEventListener('abort', onCallerAbort);

    let res: Response;
    try {
      res = await fetchImpl(buildUrl(config.baseUrl, path, options.query), {
        method: options.method ?? 'GET',
        headers,
        body:
          options.rawBody ??
          (options.body === undefined
            ? undefined
            : JSON.stringify(options.body)),
        credentials: 'include',
        ...(options.cache ? { cache: options.cache } : {}),
        ...(options.keepalive ? { keepalive: true } : {}),
        signal: timeout.signal,
      });
    } catch (cause) {
      if (options.signal?.aborted) throw cause;
      const timedOut = timeout.signal.aborted;
      throw new ApiError({
        status: 0,
        code: timedOut
          ? ClientErrorCode.TIMEOUT
          : ClientErrorCode.NETWORK_ERROR,
        message: timedOut
          ? 'The server took too long to respond.'
          : 'Cannot reach the server. Check the connection and try again.',
        requestId,
      });
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener('abort', onCallerAbort);
    }

    const responseRequestId = res.headers.get(REQUEST_ID_HEADER) ?? requestId;
    if (res.ok && options.responseType === 'blob')
      return { data: (await res.blob()) as T };
    const body = await readJson(res);

    if (!res.ok) {
      const problem = isProblemDetails(body) ? body : undefined;
      // Idle lock (RL-720): the server refuses data calls until the PIN unlocks.
      if (res.status === 423 && problem?.code === 'SESSION_LOCKED')
        config.onSessionLocked?.();
      const code = problem?.code ?? `HTTP_${res.status}`;
      if (isBranchRejection(res.status, code))
        config.onBranchRejected?.(headers[BRANCH_HEADER] ?? null);
      throw new ApiError({
        status: res.status,
        code,
        message:
          // The API's rate limiter sends "ThrottlerException: Too Many
          // Requests"; staff see plain words instead (RL-1793, #133).
          code === 'RATE_LIMITED'
            ? RATE_LIMITED_MESSAGE
            : (problem?.detail ??
              problem?.title ??
              `Request failed (${res.status})`),
        requestId: problem?.requestId ?? responseRequestId,
        problem,
      });
    }

    if (res.status === 204) return { data: undefined as T };
    if (typeof body !== 'object' || body === null || !('data' in body)) {
      throw new ApiError({
        status: res.status,
        code: ClientErrorCode.INVALID_RESPONSE,
        message: 'The server sent an unexpected response.',
        requestId: responseRequestId,
      });
    }
    return body as ApiEnvelope<T, M>;
  }

  async function request<T, M = undefined>(
    path: string,
    options: RequestOptions = {},
  ): Promise<ApiEnvelope<T, M>> {
    try {
      return await send<T, M>(path, options);
    } catch (error) {
      const canRetry =
        error instanceof ApiError &&
        error.isUnauthenticated &&
        !options.skipAuthRetry &&
        !options.anonymous &&
        config.refreshSession;
      if (!canRetry) throw error;
      const restored = await config.refreshSession!();
      if (!restored) throw error;
      return send<T, M>(path, options);
    }
  }

  return {
    request,
    get: <T, M = undefined>(
      path: string,
      options?: Omit<RequestOptions, 'method' | 'body'>,
    ) => request<T, M>(path, { ...options, method: 'GET' }),
    post: <T>(
      path: string,
      body?: unknown,
      options?: Omit<RequestOptions, 'method' | 'body'>,
    ) => request<T>(path, { ...options, method: 'POST', body }),
    patch: <T>(
      path: string,
      body?: unknown,
      options?: Omit<RequestOptions, 'method' | 'body'>,
    ) => request<T>(path, { ...options, method: 'PATCH', body }),
    put: <T>(
      path: string,
      body?: unknown,
      options?: Omit<RequestOptions, 'method' | 'body'>,
    ) => request<T>(path, { ...options, method: 'PUT', body }),
    del: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...options, method: 'DELETE' }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * Hooks wired at runtime by the session and branch providers, so the
 * app-wide client stays a plain module.
 */
export const apiHooks: Pick<
  ApiClientConfig,
  | 'getAccessToken'
  | 'getBranchId'
  | 'refreshSession'
  | 'onSessionLocked'
  | 'onBranchRejected'
> = {};

/** App-wide client. Base URL and timeout come from validated env. */
export const api: ApiClient = createApiClient({
  baseUrl: env.apiBaseUrl,
  timeoutMs: env.apiTimeoutMs,
  getAccessToken: () => apiHooks.getAccessToken?.(),
  getBranchId: () => apiHooks.getBranchId?.(),
  refreshSession: () => apiHooks.refreshSession?.() ?? Promise.resolve(false),
  onSessionLocked: () => apiHooks.onSessionLocked?.(),
  onBranchRejected: (branchId) => apiHooks.onBranchRejected?.(branchId),
});
